import { Injectable } from '@angular/core';
import { Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ViewScopeService } from './view-scope.service';
import { AuthStateService } from './auth-state.service';

import { AjaxService } from './ajax.service';
import { LOGIN_LOGOUT_NOTI } from './account-api.constants';


@Injectable({
	providedIn: 'root'
})
export class ExitService {
	private isExiting = false;

	// --- Inactivity/session timeout logic ---
	private inactivityTimeoutMinutes = 20; // X minutes
	private warningCountdownSeconds = 60;  // Y seconds
	private inactivityCountdownSub: Subscription | null = null;
	private warningCountdownSub: Subscription | null = null;
	private inactivitySecondsLeft = 0;
	private remainingCountdown = 0;
	private isDialogOpen = false;

	/** Emits { secondsLeft, expired } */
	public sessionTimeoutWarning$ = new Subject<{ secondsLeft: number; expired: boolean }>();

	constructor(
		private viewScopeService: ViewScopeService,
		private ajaxService: AjaxService,
		private authStateService: AuthStateService
	) { }

	/** Change timeout duration dynamically */
	setInactivityTimeout(minutes: number) {
		this.inactivityTimeoutMinutes = minutes;
	}

	/** Start monitoring user activity */
	startInactivityWatch() {
		console.warn('[ExitService] Inactivity watch started.');
		this.clearInactivityWatch();
		this.attachActivityListeners();
		this.resetInactivityTimer(false);
	}

	/** Stop all timers and listeners */
	stopInactivityWatch() {
		this.clearInactivityWatch();
		this.detachActivityListeners();
	}

	/** Reset inactivity timer */
	private resetInactivityTimer(isUserActivity: boolean = false) {
		if (this.inactivityCountdownSub) {
			this.inactivityCountdownSub.unsubscribe();
		}

		// Do not reset if the dialog is open (warning stage)
		if (this.isDialogOpen) return;

		this.inactivitySecondsLeft = Math.round(this.inactivityTimeoutMinutes * 60);

		this.inactivityCountdownSub = timer(0, 1000).subscribe(() => {
			if (this.inactivitySecondsLeft > 0) {
				this.inactivitySecondsLeft--;
				// console.log(`[ExitService] Inactivity: ${this.inactivitySecondsLeft} seconds left until session warning.`);
			} else {
				this.inactivityCountdownSub?.unsubscribe();
				this.showSessionTimeoutWarning();
			}
		});
	}

	/** Remove all timers and reset flags */
	private clearInactivityWatch() {
		this.inactivityCountdownSub?.unsubscribe();
		this.warningCountdownSub?.unsubscribe();
		this.inactivityCountdownSub = null;
		this.warningCountdownSub = null;
		this.isDialogOpen = false;
	}

	/** Listen for activity */
	private attachActivityListeners() {
		window.addEventListener('mousemove', this.activityHandler, true);
		window.addEventListener('keydown', this.activityHandler, true);
		window.addEventListener('mousedown', this.activityHandler, true);
		window.addEventListener('touchstart', this.activityHandler, true);
	}

	/** Stop listening for activity */
	private detachActivityListeners() {
		window.removeEventListener('mousemove', this.activityHandler, true);
		window.removeEventListener('keydown', this.activityHandler, true);
		window.removeEventListener('mousedown', this.activityHandler, true);
		window.removeEventListener('touchstart', this.activityHandler, true);
	}

	private activityHandler = () => this.resetInactivityTimer(true);

	/** Show countdown before forced sign-out */
	private showSessionTimeoutWarning() {
		this.isDialogOpen = true;
		this.remainingCountdown = this.warningCountdownSeconds;

		console.log(`[ExitService] Session timeout warning countdown started: ${this.remainingCountdown} seconds`);
		this.sessionTimeoutWarning$.next({ secondsLeft: this.remainingCountdown, expired: false });

		if (this.warningCountdownSub) {
			this.warningCountdownSub.unsubscribe();
		}

		this.warningCountdownSub = timer(1000, 1000).subscribe(() => {
			this.remainingCountdown--;
			if (this.remainingCountdown > 0) {
				console.log(`[ExitService] Session timeout warning: ${this.remainingCountdown} seconds left`);
				this.sessionTimeoutWarning$.next({ secondsLeft: this.remainingCountdown, expired: false });
			} else {
				console.log(`[ExitService] Session timeout warning: 0 seconds left (expired)`);
				this.sessionTimeoutWarning$.next({ secondsLeft: 0, expired: true });
				this.warningCountdownSub?.unsubscribe();
				this.warningCountdownSub = null;
			}
		});
	}

	/** User clicks "I'm here" — continue session safely */
	public continueSession() {
		console.log('[ExitService] Continue session clicked.');

		// Don’t trigger logout cleanup if already exiting
		if (this.isExiting) {
			console.warn('[ExitService] Skip continueSession — exit already in progress.');
			return;
		}

		this.isDialogOpen = false;

		// Stop warning countdown
		this.warningCountdownSub?.unsubscribe();
		this.warningCountdownSub = null;

		// Emit reset state to dialog
		this.sessionTimeoutWarning$.next({ secondsLeft: 0, expired: false });

		// Restart the inactivity timer
		setTimeout(() => {
			console.log('[ExitService] Session timer restarted after user interaction.');
			this.resetInactivityTimer(true);
		}, 200);
	}

	/** When session expires — log out automatically */
	public async expireSessionAndCleanup(): Promise<void> {
		if (this.isExiting) {
			console.warn('[ExitService] Already exiting, skipping duplicate cleanup.');
			return;
		}

		console.warn('[ExitService] Session expired → triggering cleanup.');
		this.isExiting = true;
		this.isDialogOpen = false;

		this.inactivityCountdownSub?.unsubscribe();
		this.warningCountdownSub?.unsubscribe();
		this.inactivityCountdownSub = null;
		this.warningCountdownSub = null;

		this.sessionTimeoutWarning$.next({ secondsLeft: 0, expired: true });

		await this.cleanExit(true, true);
	}

	/** Cleanup + backend logout + redirect. Every "not logged in" case lands on the public landing page. */
	async cleanExit(notifyApi: boolean = true, redirectToLogin: boolean = true): Promise<void>
	{
		if (this.isExiting && notifyApi) {
			// console.warn('[ExitService] cleanExit already running, skipping duplicate.');
			return;
		}

		console.log('[ExitService] inside the cleanExit: notifyApi: ', notifyApi, ' redirectToLogin: ', redirectToLogin);
		this.clearInactivityWatch();

		const finish = () =>
		{
			if (redirectToLogin)
			{
				console.log('[ExitService] Performing client cleanup + redirect.');
				this.performClientSideCleanupDirect();
				try {
					localStorage.setItem('signedOut', 'true');
					window.location.href = '/landing'; // Full navigation - clears Angular state and lands on the public landing page
				} catch (e) {
					console.error('[ExitService] Reload failed:', e);
				}
			}
		};

		if (notifyApi) {
			try {
				await this.sendExitNotification().toPromise();
			} catch (err) {
				console.error('[ExitService] Error notifying backend:', err);
			} finally {
				finish();
			}
		} else {
			finish();
		}
	}

	/** Notify backend logout endpoint */
	private sendExitNotification(): Observable<any> {
		const userId = this.viewScopeService.getCurrentOpertaingUserId().toString();
		const numericId = parseInt(userId || '0', 10);

		if (isNaN(numericId) || numericId <= 0) {
			console.warn('[ExitService] No valid userId for logout API.');
			return of(null);
		}

		// If a valid user is logged in
		if (numericId > 0)
		{
			const logoutUrl = this.ajaxService.getBaseOfApi() + LOGIN_LOGOUT_NOTI; // Build the logout URL for the API and call the logout via AjaxService
			const payload = JSON.stringify({ userid: userId }); // Create a payload with the userId in a JSON structure
			console.log(payload);

			return this.ajaxService.updateObjectData(logoutUrl, payload); // Return the observable from AjaxService
		}
		// Always return an observable
		return of(null);
	}

	/** Clean up only minimal session data — no cookie clearing */
	performClientSideCleanupDirect(): void {
		console.log('[ExitService] Minimal cleanup (preserving cookies/localStorage)');
		this.authStateService.clear();
	}
}
